// Oficinas no Google Maps (Places API, busca por texto perto do motorista).
// Só funciona com a chave do Google (GOOGLE_MAPS_CHAVE, em config.js) e com
// internet. A biblioteca do Google só é carregada quando o SOS precisa.
//
// Custo: cada busca é uma "Text Search Enterprise" (pede telefone e horário).
// O Google dá 1.000 por mês de graça; o limite por dia fica configurado no
// Google Cloud, para não passar disso.
import { GOOGLE_MAPS_CHAVE } from '../config.js';
import { distanciaKm, normalizarTelefone } from './sos.js';

export const temGoogle = () => Boolean(GOOGLE_MAPS_CHAVE);

let carregamento = null;

function carregarGoogle() {
  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google);
  if (carregamento) return carregamento;
  carregamento = new Promise((ok, erro) => {
    const nome = `__maxcargoGoogle${Date.now()}`;
    window[nome] = () => {
      delete window[nome];
      ok(window.google);
    };
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://maps.googleapis.com/maps/api/js'
      + `?key=${encodeURIComponent(GOOGLE_MAPS_CHAVE)}&v=weekly&language=pt-BR&region=BR&loading=async&callback=${nome}`;
    script.onerror = () => {
      carregamento = null;
      erro(new Error('Não foi possível abrir o Google Maps agora. Confira a internet.'));
    };
    document.head.append(script);
  });
  return carregamento;
}

// Retângulo em volta do ponto (o Google limita a busca a ele).
function retangulo({ latitude, longitude }, raioKm) {
  const dLat = raioKm / 111.32;
  const dLon = raioKm / (111.32 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.01));
  return { north: latitude + dLat, south: latitude - dLat, east: longitude + dLon, west: longitude - dLon };
}

// [{ google_place_id, nome, endereco, telefone, latitude, longitude, distancia, aberta }]
// da mais perto para a mais longe, só até o raio. `google` serve para os testes.
export async function oficinasNoGoogle({ busca, local, raioKm }, { google = null } = {}) {
  const g = google ?? (await carregarGoogle());
  const { Place, SearchByTextRankPreference } = await g.maps.importLibrary('places');
  const pedido = {
    textQuery: busca,
    fields: ['id', 'displayName', 'formattedAddress', 'location', 'nationalPhoneNumber', 'internationalPhoneNumber',
      'regularOpeningHours', 'utcOffsetMinutes', 'businessStatus'],
    locationRestriction: retangulo(local, raioKm),
    language: 'pt-BR',
    region: 'br',
    maxResultCount: 20,
  };
  if (SearchByTextRankPreference?.DISTANCE) pedido.rankPreference = SearchByTextRankPreference.DISTANCE;
  const { places = [] } = await Place.searchByText(pedido);
  const lista = [];
  for (const p of places) {
    if (p.businessStatus && p.businessStatus !== 'OPERATIONAL') continue;
    if (!p.location) continue;
    const ponto = { latitude: p.location.lat(), longitude: p.location.lng() };
    const distancia = distanciaKm(local, ponto);
    if (distancia > raioKm) continue;
    let aberta = null;
    if (p.regularOpeningHours && typeof p.isOpen === 'function') {
      try {
        aberta = (await p.isOpen()) ?? null;
      } catch {
        aberta = null;
      }
    }
    lista.push({
      google_place_id: p.id,
      nome: p.displayName ?? 'Oficina',
      endereco: p.formattedAddress ?? null,
      telefone: normalizarTelefone(p.nationalPhoneNumber ?? p.internationalPhoneNumber),
      ...ponto,
      distancia,
      aberta,
    });
  }
  return lista.sort((a, b) => a.distancia - b.distancia);
}

// Distância de carro pela estrada, em km (Routes API, pela biblioteca do
// Maps). Precisa da "Routes API" ligada no Google Cloud e liberada na chave;
// sem ela, dá erro, e o Raio-X usa a estimativa pela linha reta.
export async function distanciaPeloGoogle(origem, destino, { google = null } = {}) {
  const g = google ?? (await carregarGoogle());
  const { Route } = await g.maps.importLibrary('routes');
  const { routes } = await Route.computeRoutes({ origin: origem, destination: destino, travelMode: 'DRIVING', fields: ['distanceMeters'] });
  const metros = routes?.[0]?.distanceMeters;
  return metros > 0 ? metros / 1000 : null;
}
